import React, { useState, useEffect } from 'react';

/**
 * EmailReplyModal - Enterprise-grade email reply interface
 * 
 * Features:
 * - Full email thread view
 * - Linked inspection/lead tracking via reply token
 * - Rich compose area
 * - Sent confirmation with tracking
 */
const EmailReplyModal = ({ 
  isOpen, 
  onClose, 
  email, 
  backendUrl,
  onEmailSent 
}) => {
  const [replyText, setReplyText] = useState('');
  const [subject, setSubject] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [linkedInspection, setLinkedInspection] = useState(null);
  const [loadingInspection, setLoadingInspection] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);

  // Initialize fields when email changes
  useEffect(() => {
    if (email) {
      const originalSubject = email.subject || '';
      setSubject(originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`);
      setToEmail(email.from || '');
      setReplyText('');
      setError(null);
      
      // Load linked inspection if available
      if (email.inspection_id && email.inspection_exists) {
        loadLinkedInspection(email.inspection_id);
      } else {
        setLinkedInspection(null);
      }
    }
  }, [email]);

  const loadLinkedInspection = async (inspectionId) => {
    setLoadingInspection(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/admin/inspections/${encodeURIComponent(inspectionId)}`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await res.json();
      if (data.success && data.inspection) {
        setLinkedInspection(data.inspection);
        
        // Load conversation history from inspection's activity log
        const activityLog = data.inspection.lead_activity_log || [];
        const emailActivity = activityLog.filter(a => 
          a.type === 'admin_email_sent' || 
          a.type === 'clarification_email_sent' ||
          a.type === 'inbound_reply_received'
        );
        setConversationHistory(emailActivity);
      }
    } catch (err) {
      console.error('Failed to load linked inspection:', err);
    } finally {
      setLoadingInspection(false);
    }
  };

  const handleSend = async () => {
    if (!replyText.trim()) {
      setError('Please enter a message');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/admin/inbound-emails/${encodeURIComponent(email.id)}/send-reply`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          inspection_id: email.inspection_id,
          to_email: toEmail,
          subject: subject,
          message: replyText
        })
      });

      const data = await res.json();
      
      if (!data.success) {
        throw new Error(data.detail || data.error || 'Failed to send');
      }

      // Success!
      if (onEmailSent) {
        onEmailSent();
      }
      onClose();

    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen || !email) return null;

  // Extract reply token from email
  const replyToken = email.correlation?.item_token || 
                     (email.text?.match(/TQ-([A-Z0-9]+)/)?.[0]) || 
                     null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100000] flex items-center justify-center p-4 pt-20 pb-6">
      <div className="bg-[var(--pbfs-navy)] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-white/10 shadow-2xl flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-[var(--pbfs-navy)]/80">
          <div className="flex items-center gap-4">
            <div className="text-2xl">📧</div>
            <div>
              <h2 className="text-xl font-bold text-white">Reply to Email</h2>
              <p className="text-white/60 text-sm">
                Original from: <span className="text-white font-mono">{email.from}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-3xl leading-none hover:bg-white/10 w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Linked Lead Card */}
          {(email.inspection_id || replyToken) && (
            <div className="bg-[var(--pbfs-green)]/10 border border-[var(--pbfs-green)]/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🔗</span>
                  <div>
                    <div className="text-white font-semibold">Linked Lead</div>
                    {replyToken && (
                      <div className="text-[var(--pbfs-green)] font-mono text-sm">
                        Reply Code: {replyToken}
                      </div>
                    )}
                  </div>
                </div>
                {loadingInspection ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[var(--pbfs-green)]"></div>
                ) : linkedInspection ? (
                  <div className="text-right">
                    <div className="text-white font-semibold">
                      {linkedInspection.business_name || 'Unknown Business'}
                    </div>
                    <div className="text-white/60 text-sm">
                      {linkedInspection.site_city}, {linkedInspection.site_state}
                    </div>
                    <div className="text-white/40 text-xs mt-1">
                      Status: <span className={`font-semibold ${
                        linkedInspection.status === 'approved' ? 'text-green-400' :
                        linkedInspection.status === 'pending' ? 'text-yellow-400' :
                        'text-white/60'
                      }`}>{linkedInspection.status}</span>
                    </div>
                  </div>
                ) : email.inspection_exists === false ? (
                  <div className="text-red-400 text-sm">
                    ⚠️ Lead not found in database
                  </div>
                ) : (
                  <div className="text-white/40 text-sm">
                    ID: {email.inspection_id?.slice(0, 8)}...
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Original Email */}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 bg-white/5">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-white/60 text-sm">From:</span>
                  <span className="text-white ml-2 font-mono">{email.from}</span>
                </div>
                <div className="text-white/40 text-xs">
                  {new Date(email.received_at).toLocaleString()}
                </div>
              </div>
              <div className="mt-1">
                <span className="text-white/60 text-sm">Subject:</span>
                <span className="text-white ml-2">{email.subject || '(no subject)'}</span>
              </div>
            </div>
            <div className="p-4">
              <div className="text-white/80 whitespace-pre-wrap font-mono text-sm leading-relaxed">
                {(() => {
                  // Safely extract text from email (CodeQL: fix incomplete multi-character sanitization)
                  if (email.text) return email.text;
                  
                  if (email.html) {
                    try {
                      // Use DOMParser for safe HTML text extraction
                      const doc = new DOMParser().parseFromString(email.html, 'text/html');
                      return doc.body.textContent || '(empty message)';
                    } catch {
                      // Fallback: multiple passes to ensure complete tag removal
                      let cleaned = email.html;
                      let prevLength = -1;
                      while (cleaned.length !== prevLength) {
                        prevLength = cleaned.length;
                        cleaned = cleaned.replace(/<[^>]*>/g, '');
                      }
                      return cleaned || '(empty message)';
                    }
                  }
                  
                  return '(empty message)';
                })()}
              </div>
            </div>
            
            {/* AI Analysis */}
            {email.analysis && (
              <div className="px-4 py-3 border-t border-white/10 bg-blue-500/5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-blue-400">🤖</span>
                  <span className="text-blue-400 font-semibold text-sm">AI Analysis</span>
                  <span className="text-white/40 text-xs">
                    ({Math.round((email.analysis.confidence || 0) * 100)}% confident)
                  </span>
                </div>
                <div className="text-white/70 text-sm">
                  <strong>Intent:</strong> {email.analysis.intent?.replace(/_/g, ' ')}
                </div>
                <div className="text-white/70 text-sm mt-1">
                  <strong>Summary:</strong> {email.analysis.summary}
                </div>
                {email.analysis.recommended_message_to_user && (
                  <div className="mt-2 p-2 bg-white/5 rounded-lg">
                    <div className="text-white/50 text-xs mb-1">Suggested Reply:</div>
                    <div className="text-white/80 text-sm italic">
                      "{email.analysis.recommended_message_to_user}"
                    </div>
                    <button
                      onClick={() => setReplyText(email.analysis.recommended_message_to_user)}
                      className="mt-2 text-xs text-[var(--pbfs-blue)] hover:text-[var(--pbfs-blue)]/80"
                    >
                      Use this suggestion →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Conversation History */}
          {conversationHistory.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-white/60 text-sm font-semibold mb-3">
                📜 Previous Activity ({conversationHistory.length})
              </div>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {conversationHistory.slice(-5).map((activity, idx) => (
                  <div key={idx} className="text-sm border-l-2 border-white/20 pl-3">
                    <div className="text-white/40 text-xs">
                      {new Date(activity.at).toLocaleString()}
                    </div>
                    <div className="text-white/70">
                      {activity.type === 'admin_email_sent' && `📤 Sent to ${activity.to}`}
                      {activity.type === 'clarification_email_sent' && `📤 Clarification request sent`}
                      {activity.type === 'inbound_reply_received' && `📥 Reply received`}
                    </div>
                    {activity.message_preview && (
                      <div className="text-white/50 text-xs truncate">
                        {activity.message_preview.slice(0, 100)}...
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Compose Reply */}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 bg-white/5">
              <div className="text-white font-semibold">✍️ Your Reply</div>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">To</label>
                <input
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="recipient@email.com"
                  className="w-full bg-[#00314F] border border-white/20 rounded-lg px-4 py-2 text-white font-mono focus:outline-none focus:border-[var(--pbfs-blue)] focus:ring-2 focus:ring-[var(--pbfs-blue)]/50"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full bg-[#00314F] border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)] focus:ring-2 focus:ring-[var(--pbfs-blue)]/50"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Message</label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply here..."
                  rows={6}
                  className="w-full bg-[#00314F] border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[var(--pbfs-blue)] focus:ring-2 focus:ring-[var(--pbfs-blue)]/50 resize-none font-mono"
                />
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
              <div className="text-red-400">❌ {error}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 bg-[var(--pbfs-navy)]/80 flex items-center justify-between">
          <div className="text-white/40 text-sm">
            Sending from: <span className="font-mono text-white/60">tagquest@pyebarkerfs.com</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !replyText.trim()}
              className="px-6 py-2 rounded-lg bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 disabled:bg-white/10 disabled:cursor-not-allowed text-white font-semibold transition-colors flex items-center gap-2"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <span>📤</span>
                  Send Reply
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailReplyModal;

